import { DatePipe, NgFor, NgIf } from '@angular/common'
import { Component } from '@angular/core'
import {
    FormBuilder,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { DateAdapter, MatNativeDateModule } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import {
    MatError,
    MatFormField,
    MatFormFieldModule,
    MatLabel,
} from '@angular/material/form-field'
import { MatIcon, MatIconModule } from '@angular/material/icon'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { MatStepperModule } from '@angular/material/stepper'
import { SearchSelectComponent } from '../../components/search-select/search-select.component'
import { identificationTypes, sex } from '../incident-observatorio/incident-form/incident.definitions'
import { PersonService } from '../../services/person/persona.service'
import { Outsider, OutsiderDTO } from '../../models/outsider.interface'
import { MatDialogRef } from '@angular/material/dialog'
import { OutsiderService } from '../../services/outsider/outsider.service'
import { ToastrService } from 'ngx-toastr'
import { Location } from '../../models/location.interface'
import { LocationService } from '../../services/location/location.service'
import { TrackingService } from '../../services/tracking/tracking.service'
import { Subject, takeUntil } from 'rxjs'

@Component({
    selector: 'app-outsider-form',
    standalone: true,
    imports: [
        MatStepperModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatIcon,
        MatIconModule,
        MatCheckboxModule,
        MatRadioModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgIf,
        NgFor,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        SearchSelectComponent,
    ],
    providers: [DatePipe],
    templateUrl: './outsider-form.component.html',
    styleUrl: './outsider-form.component.css',
})
export class OutsiderFormComponent {
    selectedOutsider: Outsider | null = null
    outsiderId: string | null = null
    identificationTypes = identificationTypes
    sex = sex
    errorMsg: string[] | null = null
    locations: Location[] = []
    displayFn = (location: Location) => location?.city ?? ''
    idFn = (location: Location) => location.id
    constructor(
        private fb: FormBuilder,
        private personService: PersonService,
        private outsiderService: OutsiderService,
        private toastr: ToastrService,
        private datePipe: DatePipe,
        private locationService: LocationService,
        private trackingService: TrackingService,
        private dialogRef: MatDialogRef<OutsiderFormComponent>,
        private adapter: DateAdapter<Date>
    ) {
        this.adapter.setLocale('es-CO')
    }

    ngOnInit(): void {
        this.editOutsiderForm
            .get('birthDate')
            ?.valueChanges.subscribe((birth: Date | null) => {
                const age = this.outsiderService.calculateAge(birth)
                this.editOutsiderForm
                    .get('age')
                    ?.setValue(age ?? 0, { emitEvent: false })
            })

        this.locationService.allLocation().subscribe((data) => {
            this.locations = data

            this.outsiderService.$outsidersSelected.subscribe((outsider) => {
                if (outsider) {
                    this.outsiderId = String(outsider.id)
                    this.selectedOutsider = outsider
                    this.editOutsiderForm.patchValue({
                        ...outsider,
                        socioeconomicStatus: outsider.socioeconomicStatus?.id,
                        profesion: outsider.profesion?.id,
                        socialSecurity: outsider.socialSecurity?.id,
                        education: outsider.education?.id,
                        address: outsider.address?.id,
                        location: outsider.location ?? null,
                        placeBirth: outsider.placeBirth ?? null,
                        birthDate: this.trackingService.toLocalDateOnly(
                            outsider.birthDate
                        ),
                    })
                }
            })
        })
    }

    searchPerson = (value: string) => {
        return this.personService.searchPerson(value)
    }
    showPerson = (person: Outsider) =>
        `${person.identificationCode} - ${person.name} ${person.lastName}`
    personIdentifier = (person: Outsider) => person.identificationCode

    fillFields(person: Outsider) {
        this.editOutsiderForm.patchValue({
            name: person.name,
            lastName: person.lastName,
            identificationType: person.identificationType ?? '',
        })
    }
    editOutsiderForm = this.fb.group({
        personRef: ['', []],
        identificationCode: [0, []],
        identificationType: ['', []],
        name: ['', []],
        lastName: ['', []],
        phone: ['', []],
        birthDate: this.fb.control<Date | null>(null),
        age: [0, []],
        socioeconomicStatus: [0, []],
        profesion: [0, []],
        socialSecurity: [0, []],
        education: [0, []],
        address: [0, []],
        location: this.fb.control<Location | null>(null),
        placeBirth: this.fb.control<Location | null>(null),
        sex: ['', []],
        email: [
            '',
            [
                Validators.pattern(
                    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                ),
            ],
        ],
    })

    closeDialog() {
        this.dialogRef.close()
    }

    updateOutsider() {
        if (this.editOutsiderForm.invalid || !this.outsiderId) return
        if (this.editOutsiderForm.pristine) {
            this.dialogRef.close()
            return
        }
        const form = this.editOutsiderForm.value
        const formattedDate = this.datePipe.transform(
            form.birthDate,
            'yyyy-MM-dd'
        )

        const payload: OutsiderDTO = {
            ...form,
            location: form.location ?? null,
            placeBirth: form.placeBirth ?? null,
            birthDate: formattedDate,
        } as any

        const id = Number(this.outsiderId)
        this.outsiderService.updateOutsider(id, { ...payload, id }).subscribe({
            next: () => {
                this.toastr.success(
                    'La persona fue EDITADA correctamente!',
                    'Exitoso!',
                    {
                        progressBar: true,
                        timeOut: 5000,
                    }
                )
                this.dialogRef.close()
            },
            error: () => {
                if (this.errorMsg) {
                    this.toastr.error('Error: ' + this.errorMsg, undefined, {
                        progressBar: true,
                        timeOut: 5000,
                    })
                }
            },
        })
    }
}
